/**
 * The second channel: a shape per runway colour.
 *
 * The only mechanic in the game is matching a colour, which leaves red-green
 * colour blindness (~8% of men) with no readable signal at all. Every place
 * that carries a colour — runway threshold, aircraft body, HUD badge — can
 * carry the matching shape instead, so the pairing survives without the hue.
 *
 * Shapes are described as regular polygons rather than glyphs so the same
 * definition drives the scene geometry and the HUD text.
 */

import type { RunwayColor } from "./palette";

export interface RunwaySymbol {
  /** Corner count of the regular polygon; 16 reads as a disc. */
  readonly sides: number;
  /** Rotation in radians that puts the shape in its canonical pose. */
  readonly rotation: number;
  /** Text stand-in, used by the HUD badges and screen-reader copy. */
  readonly glyph: string;
}

export const RUNWAY_SYMBOLS = Object.freeze({
  // Point up.
  yellow: Object.freeze({ sides: 3, rotation: Math.PI / 2, glyph: "▲" }),
  blue: Object.freeze({ sides: 16, rotation: 0, glyph: "●" }),
  // Flat-topped, not a diamond.
  red: Object.freeze({ sides: 4, rotation: Math.PI / 4, glyph: "■" }),
}) as Readonly<Record<RunwayColor, RunwaySymbol>>;

/**
 * Sizes, relative to the thing the badge is drawn on.
 *
 * The runway shape gets the same pale disc the aircraft badge has. Stamped
 * straight onto the threshold it was the runway's own dark tone on the
 * runway's own bright tone, and at 1/2.4 render scale that pairing is a few
 * muddy pixels — the one place the second channel is needed most was the one
 * place it could not be read.
 */
export const SYMBOL_SIZING = Object.freeze({
  /** Symbol radius as a fraction of the runway width. */
  runwayThreshold: 0.3,
  /**
   * Radius of the disc behind it, same fraction. Larger than the aircraft
   * badge on purpose: this one lies flat on the ground, and the oblique camera
   * squashes a ground disc to about two thirds of the height a floating badge
   * of the same radius keeps.
   */
  runwayThresholdDisc: 0.5,
  /** Badge radius in world units, sitting over the fuselage. */
  aircraftBadge: 1.55,
  /** How far above the aircraft the badge floats, in world units. */
  aircraftBadgeHeight: 2.6,
});

/** Pale ground both badges are drawn on, so the shape carries the contrast. */
export const SYMBOL_BADGE_COLOR = 0xf3f6f8;
